/**
 * UsersList component - displays all family members
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users as UsersIcon, AlertCircle, ArrowLeft, Trash2 } from 'lucide-react';
import { useAllUsers, useDeleteUser } from '../../services/users';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export const UsersList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { data, isLoading, isError, error, refetch } = useAllUsers();
  const deleteUserMutation = useDeleteUser();

  const [userToDelete, setUserToDelete] = useState<{ userId: string; displayName: string } | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isAdmin = currentUser?.isAdmin === true;

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteError(null);

    try {
      await deleteUserMutation.mutateAsync(userToDelete.userId);
      setUserToDelete(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete user');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading family members...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>Failed to load family members. {error?.message}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Filter out admin users
  const users = (data?.items || []).filter((user) => !user.isAdmin);
  const total = users.length;

  if (users.length === 0) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-4 py-12 text-center">
        <UsersIcon className="h-16 w-16 text-muted-foreground" />
        <h3 className="text-lg font-semibold">No family members yet</h3>
        <p className="text-sm text-muted-foreground">
          Family members will appear here once they create accounts.
        </p>
      </div>
    );
  }

  // Sort users alphabetically by display name
  const sortedUsers = [...users].sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <>
      <div className="mx-auto w-full max-w-6xl" data-testid="users-list">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-2xl font-bold">Family Members</h2>
          </div>
          <p className="text-muted-foreground">
            {total} {total === 1 ? 'member' : 'members'} in your family circle
          </p>
        </div>

        {/* Error message */}
        {deleteError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        {/* Grid of user cards */}
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          role="list"
          aria-label="Family members list"
        >
          {sortedUsers.map((user) => (
            <Card
              key={user.userId}
              className="group relative"
              data-testid="user-card"
              role="listitem"
            >
              <CardContent
                className="cursor-pointer p-6 transition-colors hover:bg-accent"
                onClick={() => navigate(`/profile/${user.userId}`)}
              >
                <div className="flex flex-col items-center gap-3 text-center">
                  {/* Avatar */}
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user.avatarUrl} alt={`${user.displayName}'s avatar`} />
                    <AvatarFallback className="text-2xl">
                      {user.displayName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  {/* User info */}
                  <div className="w-full">
                    <h3 className="truncate text-lg font-semibold">{user.displayName}</h3>
                    <p className="truncate text-sm text-muted-foreground">{user.email}</p>
                  </div>

                  {/* Join date */}
                  <p className="text-xs text-muted-foreground">
                    Joined {formatJoinDate(user.createdAt)}
                  </p>
                </div>
              </CardContent>

              {/* Delete button (admin only) */}
              {isAdmin && (
                <div className="absolute right-2 top-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUserToDelete({ userId: user.userId, displayName: user.displayName });
                    }}
                    aria-label={`Delete ${user.displayName}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.displayName}</strong>?
              <br />
              <br />
              This will permanently delete:
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>User profile and account</li>
                <li>All photos uploaded by this user</li>
                <li>All comments and likes</li>
                <li>All notifications</li>
              </ul>
              <br />
              <strong className="text-destructive">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteUserMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleteUserMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteUserMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Format join date to relative or absolute format
 */
function formatJoinDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7) {
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    return `${diffDays} days ago`;
  }

  // Format as "Jan 2025"
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
